import {
  CommonApiParams,
  FILTER_KEYS,
  FilterDetailsParams,
  FilterParams,
} from "types/common";

import { USER_GROUPS } from "constants/roles";
import { changeSort } from "utils/general";
import { PATIENTS_LIMIT } from "constants/dashboard";
import { PatientRegionsRepository } from "repositories/PatientRegionsRepository";
import { convertFilterParamsToQueryObject } from "utils/formatters";
import { addRequest, removeRequest } from "store/LoadingStore/loadingSlice";
import { AppThunk } from "../rootStore";
import {
  setJointFilterParams,
  removeCurrentPatientRegion,
  setOffset,
  setPatientRegions,
  setSortConfig,
  setTotal,
  setLoading,
} from "./patientRegionsSlice";
import { Requests } from "../../types/requests";

const stringifyArray = (array: never[]) => {
  return (
    "[" +
    array
      .map((item: string) => {
        return item.trim();
      })
      .join(",") +
    "]"
  );
};

const parsePayload = (currentFilters: any) => {
  return {
    sports: currentFilters?.sports.length
      ? stringifyArray(currentFilters.sports)
      : undefined,
    teams: currentFilters.teams.length
      ? stringifyArray(currentFilters.teams)
      : undefined,
    positions: currentFilters.positions.length
      ? stringifyArray(currentFilters.positions)
      : undefined,
    examTypes: currentFilters.examTypes.length
      ? stringifyArray(currentFilters.examTypes)
      : undefined,
    genders: currentFilters.genders.length
      ? stringifyArray(currentFilters.genders)
      : undefined,
    timeRange: currentFilters.timeRange || undefined,
    fromFilterModal: true,
  };
};

/** ====== PATIENT LIST ========= * */

export const initPatientRegionsPage =
  (currentClinician?: any): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setOffset(0));

      if (currentClinician?.role?.includes(USER_GROUPS.COACH)) {
        dispatch(
          getPatientRegionsList(undefined, {
            sports:
              "[" +
              currentClinician.profileInfo.sports
                .map((item: string) => {
                  return item.trim();
                })
                .join(",") +
              "]",
          })
        );
      } else {
        dispatch(getPatientRegionsList());
      }
      dispatch(initJointFilters());
    } catch (error) {
      console.log(error);
    }
  };

export const getPatientRegionsList =
  (searchingFor?: string, detailedFilter?: FilterDetailsParams): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      const patientRegionsRepository = PatientRegionsRepository.getInstance();
      const {
        patientRegions: { limit, offset, sortConfig, jointFilterParams },
      } = getState();
      dispatch(addRequest(Requests.getPatientRegions));

      const filterParamsQueryObject =
        convertFilterParamsToQueryObject(jointFilterParams);

      const newOffset = detailedFilter?.fromFilterModal ? 0 : offset;

      const apiParams: CommonApiParams = {
        ...filterParamsQueryObject,
        ...(sortConfig.sortBy !== "" && {
          sortBy: sortConfig.sortBy,
          sortDirection: sortConfig.sortDirection,
        }),
        limit,
        offset: newOffset,
      };

      let currentFilters = detailedFilter;

      if (!currentFilters && window.localStorage.getItem("athleteFilters")) {
        const savedFilters = JSON.parse(
          window.localStorage.getItem("athleteFilters") ?? ""
        );
        currentFilters = parsePayload(savedFilters);
      }

      const patientRegions = await patientRegionsRepository.getPatientRegions(
        apiParams,
        searchingFor,
        currentFilters
      );
      dispatch(setTotal(patientRegions?.totalCount));
      dispatch(setOffset(newOffset));
      dispatch(setPatientRegions(patientRegions.items));
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(setLoading(false));
      dispatch(removeRequest(Requests.getPatientRegions));
    }
  };

export const changeSortOfPatientRegionsList =
  (sortBy: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      const {
        patientRegions: { sortConfig, limit, offset },
      } = getState();

      const changedSortConfig = changeSort(sortBy, sortConfig);
      // to save quantity of existing elements
      // dispatch(setLimit(limit + offset));

      dispatch(setSortConfig(changedSortConfig));
      const athleteFiltersRaw =
        window.localStorage.getItem("athleteFilters") || "";
      let athleteFilters: {
        sports: never[];
        teams: never[];
        positions: never[];
        genders: never[];
      } = {
        sports: [],
        teams: [],
        positions: [],
        genders: [],
      };
      try {
        athleteFilters = JSON.parse(athleteFiltersRaw);
      } catch (error) {}

      dispatch(
        getPatientRegionsList(undefined, {
          sports: athleteFilters?.sports.length
            ? stringifyArray(athleteFilters.sports)
            : undefined,
          teams: athleteFilters.teams.length
            ? stringifyArray(athleteFilters.teams)
            : undefined,
          positions: athleteFilters.positions.length
            ? stringifyArray(athleteFilters.positions)
            : undefined,

          genders: athleteFilters.genders.length
            ? stringifyArray(athleteFilters.genders)
            : undefined,
        })
      );
    } catch (error) {
      console.log(error);
    }
  };

export const goToPagePatientRegions =
  (page: number, detailedFilter?: any): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      dispatch(addRequest(Requests.getPatientRegions));

      const patientRegionsRepository = PatientRegionsRepository.getInstance();
      const {
        patientRegions: { limit, sortConfig },
      } = getState();

      dispatch(addRequest(Requests.loadMorePatientRegions));
      const newOffset = (page - 1) * limit;

      const apiParams: CommonApiParams = {
        ...(sortConfig.sortBy !== "" && {
          sortBy: sortConfig.sortBy,
          sortDirection: sortConfig.sortDirection,
        }),
        limit,
        offset: newOffset,
      };

      const patientRegions = await patientRegionsRepository.getPatientRegions(
        apiParams,
        undefined,
        detailedFilter || {}
      );

      dispatch(setPatientRegions(patientRegions.items));
      dispatch(setTotal(patientRegions.totalCount));
      dispatch(setOffset(newOffset));
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(removeRequest(Requests.getPatientRegions));
      dispatch(setLoading(false));
    }
  };

/** ====== JOINTS ========= * */

export const initJointFilters = (): AppThunk => async (dispatch) => {
  try {
    const patientRegionsRepository = PatientRegionsRepository.getInstance();
    const joints = await patientRegionsRepository.getJoints();

    const jointFilterParams: FilterParams[] = joints.map((joint: any) => ({
      id: joint._id,
      title: `${joint.side} ${joint.name}`,
      checked: false,
      filterKey: FILTER_KEYS.joint,
    }));

    dispatch(setJointFilterParams(jointFilterParams));
  } catch (error) {
    console.log(error);
  }
};
export const changeJointFilterParams =
  (jointFilterParams: FilterParams[]): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setJointFilterParams(jointFilterParams));
      dispatch(setOffset(0));

      dispatch(getPatientRegionsList());
    } catch (error) {
      console.log(error);
    }
  };

export const clearCurrentPatientRegion =
  (callback?: () => void): AppThunk =>
  (dispatch) => {
    try {
      dispatch(removeCurrentPatientRegion());
    } catch (error) {
      console.log("Error from clearCurrentPatientRegion Thunk", error);
    } finally {
      if (callback) callback();
    }
  };
